<template>
	<v-menu
		v-model="isMenuOpen"
		:close-on-content-click="true"
		:z-index="zIndex"
		:style="{ zIndex }"
		:offset="[source === 'annotation' ? -48 : 0, source === 'annotation' ? -48 : 0]"
		transition="scale-transition">
		<v-card class="color-picker-card pa-2" elevation="6">
			<div class="color-picker ga-1">
				<div
					v-for="(color, index) in colors"
					:key="index"
					v-test="`tag-editor-color-picker-swatch-${color.replace('#', '')}`"
					v-track="`tag-editor-color-picker-swatch-${color.replace('#', '')}`"
					tabindex="0"
					role="button"
					:aria-label="`Select color ${color}`"
					class="cursor-pointer scale-transition color-swatch-hover"
					:style="{ backgroundColor: color }"
					tile
					@click.stop="selectColor(color, $event)"
					@keydown.enter.prevent="selectColor(color)"
					@keydown.space.prevent="selectColor(color)"></div>
			</div>
		</v-card>
		<template #activator="{ props }">
			<div
				v-if="source !== 'annotation'"
				ref="activator"
				v-test="`tag-editor-color-picker-button`"
				v-track="`tag-editor-color-picker-button`"
				tabindex="0"
				role="button"
				aria-label="Pick color"
				class="activator rounded-circle border-solid color-swatch-hover scale-transition flex-grow-0 flex-shrink-0"
				:style="{ backgroundColor: selectedColor || getRandomColor() }"
				@click.stop
				@keydown.enter.prevent="isMenuOpen = !isMenuOpen"
				@keydown.space.prevent="isMenuOpen = !isMenuOpen"
				v-bind="props"></div>
			<div v-else ref="activator" v-bind="props" class="pa-3">
				<svg width="28" height="26" viewBox="0 0 28 26" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g clip-path="url(#paint0_angular_3507_3150_clip_path)" data-figma-skip-parse="true">
						<g transform="matrix(0 0.0125 -0.013 0 14 13)">
							<foreignObject x="-1080" y="-1080" width="2160" height="2160"
								><div
									xmlns="http://www.w3.org/1999/xhtml"
									style="
										background: conic-gradient(
											from 90deg,
											rgba(251, 21, 21, 1) 0deg,
											rgba(0, 112, 255, 1) 126.346deg,
											rgba(255, 184, 0, 1) 235.385deg,
											rgba(2, 181, 84, 1) 315deg,
											rgba(251, 21, 21, 1) 360deg
										);
										height: 100%;
										width: 100%;
										opacity: 1;
									"></div
							></foreignObject>
						</g>
					</g>
					<ellipse
						cx="14"
						cy="13"
						rx="13"
						ry="12.5"
						data-figma-gradient-fill="{&#34;type&#34;:&#34;GRADIENT_ANGULAR&#34;,&#34;stops&#34;:[{&#34;color&#34;:{&#34;r&#34;:0.9857565164566040,&#34;g&#34;:0.082933932542800903,&#34;b&#34;:0.082933932542800903,&#34;a&#34;:1.0},&#34;position&#34;:0.0},{&#34;color&#34;:{&#34;r&#34;:0.0,&#34;g&#34;:0.43921568989753723,&#34;b&#34;:1.0,&#34;a&#34;:1.0},&#34;position&#34;:0.35096153616905212},{&#34;color&#34;:{&#34;r&#34;:1.0,&#34;g&#34;:0.72156864404678345,&#34;b&#34;:0.0,&#34;a&#34;:1.0},&#34;position&#34;:0.65384614467620850},{&#34;color&#34;:{&#34;r&#34;:0.0090976618230342865,&#34;g&#34;:0.71154147386550903,&#34;b&#34;:0.33222180604934692,&#34;a&#34;:1.0},&#34;position&#34;:0.8750}],&#34;stopsVar&#34;:[{&#34;color&#34;:{&#34;r&#34;:0.9857565164566040,&#34;g&#34;:0.082933932542800903,&#34;b&#34;:0.082933932542800903,&#34;a&#34;:1.0},&#34;position&#34;:0.0},{&#34;color&#34;:{&#34;r&#34;:0.0,&#34;g&#34;:0.43921568989753723,&#34;b&#34;:1.0,&#34;a&#34;:1.0},&#34;position&#34;:0.35096153616905212},{&#34;color&#34;:{&#34;r&#34;:1.0,&#34;g&#34;:0.72156864404678345,&#34;b&#34;:0.0,&#34;a&#34;:1.0},&#34;position&#34;:0.65384614467620850},{&#34;color&#34;:{&#34;r&#34;:0.0090976618230342865,&#34;g&#34;:0.71154147386550903,&#34;b&#34;:0.33222180604934692,&#34;a&#34;:1.0},&#34;position&#34;:0.8750}],&#34;transform&#34;:{&#34;m00&#34;:1.5920408818909386e-15,&#34;m01&#34;:-26.0,&#34;m02&#34;:27.0,&#34;m10&#34;:25.0,&#34;m11&#34;:1.5308085524965700e-15,&#34;m12&#34;:0.50},&#34;opacity&#34;:1.0,&#34;blendMode&#34;:&#34;NORMAL&#34;,&#34;visible&#34;:true}" />
					<defs>
						<clipPath id="paint0_angular_3507_3150_clip_path">
							<ellipse cx="14" cy="13" rx="13" ry="12.5" />
						</clipPath>
					</defs>
				</svg>
			</div>
		</template>
	</v-menu>
</template>
<script lang="ts">
	import { getRandomColor } from "@/helpers/tags";
	import { Colors } from "@/types/tags/Colors";
	import { ColorPickerSource } from "@/types/media/ColorPickerSource";
	import { PropType } from "vue";

	export default {
		name: "ColorPicker",
		props: {
			zIndex: {
				type: Number,
				default: 99999
			},
			modelValue: {
				type: String,
				default: ""
			},
			source: {
				type: String as PropType<ColorPickerSource>,
				default: "tag"
			}
		},
		data() {
			return {
				selectedColor: this.modelValue,
				isMenuOpen: false,
				colors: Colors
			};
		},
		watch: {
			modelValue(newValue) {
				if (!newValue) {
					this.selectedColor = this.getRandomColor();
				} else {
					this.selectedColor = newValue;
				}
				this.$emit("update:modelValue", this.selectedColor);
			}
		},
		beforeMount() {
			if (!this.modelValue) {
				this.selectedColor = this.getRandomColor();
				this.$emit("update:modelValue", this.selectedColor);
			}
		},
		methods: {
			selectColor(color: string, event?: MouseEvent): void {
				if (event) {
					event.stopPropagation();
				}
				this.selectedColor = color;
				this.$emit("update:modelValue", color);
				this.isMenuOpen = false;
			},
			getRandomColor(): string {
				this.selectedColor = getRandomColor(this.selectedColor);
				this.$emit("update:modelValue", this.selectedColor);
				return this.selectedColor;
			}
		}
	};
</script>

<style scoped>
	.activator {
		width: 28px;
		height: 28px;
	}

	.color-picker {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		grid-template-rows: repeat(4, 1fr);
		width: 180px;
		height: 180px;
	}
</style>
